import { safeValue } from '@shared/utils/resource';
import { HttpClient, httpResource } from '@angular/common/http';
import { computed, effect, inject, Service, signal } from '@angular/core';
import { environment } from '../../../environments/environment';
import { UserService } from './user.service';

export type NotificationLevel = 'Error' | 'Warning' | 'Information' | 'Promo';

export interface NotificationDto {
  id: number;
  title: string;
  message: string;
  link: string;
  notificationType: NotificationLevel;
  readAt?: string;
  createdAt: string;
}

@Service()
export class NotificationService {
  private readonly http = inject(HttpClient);
  private readonly userService = inject(UserService);

  /** httpResource para carregar notificações automaticamente quando autenticado */
  readonly notificationsResource = httpResource<NotificationDto[]>(() => {
    if (!this.userService.accessToken()) return undefined;
    return `${environment.API_HOST}/Notification`;
  });

  /** Notificações com fallback para array vazio */
  readonly notifications = computed(
    () => safeValue(this.notificationsResource) ?? [],
  );

  /** Notificações não lidas */
  readonly unreadNotifications = computed(() =>
    this.notifications().filter((n) => !n.readAt),
  );

  /** Contagem de não lidas */
  readonly unreadCount = computed(() => this.unreadNotifications().length);

  /** Verifica se existe alguma notificação de erro não lida */
  readonly hasUnreadError = computed(() =>
    this.unreadNotifications().filter((n) => n.notificationType === 'Error'),
  );

  /** Notificação ativa para exibir no modal */
  readonly activeErrorNotification = signal<NotificationDto | null>(null);

  /**
   * Erros que o usuário já dispensou nesta sessão.
   *
   * Sem este registro, "dispensado" não existe como estado: o modal era função pura de
   * `hasUnreadError()`, e a única forma de dizer "já vi isso" era o `readAt` chegar do
   * servidor. Enquanto ele não chegasse — o `markAsRead` só atualiza o resource 500 ms
   * depois, e se o POST falhar nunca atualiza — qualquer coisa que reexecutasse o effect
   * trazia o modal de volta. Marcar OUTRA notificação como lida pelo sino era o caminho
   * mais fácil de reproduzir: o `update()` do resource acordava o effect, o erro seguia
   * sem `readAt` e o modal reabria sozinho.
   *
   * Agora o fechamento vale por si. A persistência continua sendo tentada, mas a decisão
   * do usuário não depende mais dela.
   */
  private readonly dismissedErrorIds = signal<ReadonlySet<number>>(new Set());

  constructor() {
    // Exibe o modal do primeiro erro não lido que ainda não foi dispensado nesta sessão.
    effect(() => {
      const dismissed = this.dismissedErrorIds();
      const next = this.hasUnreadError().find((n) => !dismissed.has(n.id));
      if (next) {
        this.activeErrorNotification.set(next);
      }
    });
  }

  /** Recarrega as notificações */
  reload(): void {
    this.notificationsResource.reload();
  }

  /** Marca uma notificação como lida */
  markAsRead(notificationId: number): void {
    this.http
      .post<NotificationDto>(
        `${environment.API_HOST}/Notification/${notificationId}/Read`,
        {},
      )
      .subscribe((updated) => {
        setTimeout(() => {
          this.notificationsResource.update((current) =>
            current?.map((n) => (n.id === updated.id ? updated : n)),
          );
        }, 500);
      });
  }

  /** Marca todas as notificações como lidas */
  markAllAsRead(): void {
    this.http
      .post<NotificationDto[]>(
        `${environment.API_HOST}/Notification/ReadAllUnreaded`,
        {},
      )
      .subscribe(() => this.notificationsResource.reload());
  }

  /**
   * Exibe uma notificação específica no modal.
   *
   * Reabrir manualmente (pela lista em Configurações) desfaz o dispensar: sem isto, clicar
   * numa notificação já dispensada não abriria nada, porque o effect a ignoraria e o
   * fechamento seguinte a colocaria de volta no registro.
   */
  showNotificationModal(notification: NotificationDto): void {
    this.dismissedErrorIds.update((ids) => {
      if (!ids.has(notification.id)) return ids;
      const next = new Set(ids);
      next.delete(notification.id);
      return next;
    });
    this.activeErrorNotification.set(notification);
  }

  /** Fecha o modal de notificação e marca como lida */
  dismissNotificationModal(markAsRead = true): void {
    const current = this.activeErrorNotification();
    this.activeErrorNotification.set(null);
    if (!current) return;

    // Registra antes do POST: o fechamento vale mesmo se a persistência falhar.
    this.dismissedErrorIds.update((ids) => new Set(ids).add(current.id));

    if (markAsRead) {
      this.markAsRead(current.id);
    }
  }

  /** Reseta o estado (usado no logout) */
  reset(): void {
    this.activeErrorNotification.set(null);
    this.dismissedErrorIds.set(new Set());
  }
}
